import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Moon, Bell, Save, AlertCircle, Clock, Sun, CloudSnow } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useSettingsStore } from "../../store/settingsStore";
import { useProfile } from "../../hooks/useProfile";
import { useDebounce } from "../../utils/debounce";
import LanguageSelector from "../LanguageSelector";

const settingItems = [
  {
    id: 'darkMode',
    icon: Moon,
    color: 'purple',
    translationKey: 'settings.darkMode'
  },
  {
    id: 'notifications',
    icon: Bell,
    color: 'green',
    translationKey: 'settings.notifications'
  }
];

// Hour options for dropdown
const hourOptions = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: `${i.toString().padStart(2, '0')}:00`
}));

export function AppSettings() {
  const { t } = useTranslation();
  const { darkMode, language, notifications, notificationPreferences } = useSettingsStore();

  console.log("AppSettings", { darkMode, language, notifications, notificationPreferences });

  const {
    updateProfile,
    setNotificationTime,
    toggleNotificationPeriod,
    fetchNotificationPreferences,
    isLoading,
    error
  } = useProfile();
  const [isSaving, setIsSaving] = useState(false);

  const [settings, setSettings] = useState({
    darkMode,
    language,
    notifications,
  });

  // Load notification preferences on mount
  useEffect(() => {
    fetchNotificationPreferences();
  }, []);

  const debouncedUpdate = useDebounce(async (data) => {
    try {
      setIsSaving(true);
      await updateProfile(data);
    } catch (err) {
      console.error(t("settings.error"), err);
    } finally {
      setIsSaving(false);
    }
  }, 1000);

  useEffect(() => {
    if (
      settings.darkMode !== darkMode ||
      settings.language !== language ||
      settings.notifications !== notifications
    ) {
      debouncedUpdate(settings);
    }
  }, [settings]);

  const handleToggle = (setting) => {
    setSettings((prev) => ({ ...prev, [setting]: !prev[setting] }));
  };

  const handleNotificationPeriodToggle = async (period: 'morning' | 'evening') => {
    if (!notificationPreferences) return;
    const currentEnabled = notificationPreferences[period].enabled;
    await toggleNotificationPeriod(period, !currentEnabled);
  };

  const handleHourChange = async (period: 'morning' | 'evening', hour: number) => {
    await setNotificationTime(period, hour);
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t("settings.title")}
        </h3>
        <AnimatePresence>
          {isSaving && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400"
            >
              <Save className="w-4 h-4 animate-pulse" />
              <span>{t('settings.saving')}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center space-x-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 text-red-700 dark:text-red-400 rounded-lg"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid gap-4">
        {settingItems.map(({ id, icon: Icon, color, translationKey }) => (
          <motion.div
            key={id}
            layout
            className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg bg-${color}-50 dark:bg-${color}-900/20`}>
                  <Icon className={`w-5 h-5 text-${color}-500 dark:text-${color}-400`} />
                </div>
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {t(translationKey)}
                </span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings[id]}
                  onChange={() => handleToggle(id)}
                  disabled={isLoading}
                  className="sr-only peer"
                />
                <div className="w-12 h-7 bg-gray-200 dark:bg-gray-700 
                  peer-focus:outline-none peer-focus:ring-4 
                  peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 
                  rounded-full peer transition-colors
                  peer-checked:bg-blue-500 dark:peer-checked:bg-blue-600
                  after:content-[''] after:absolute after:top-[2px] 
                  after:left-[2px] after:bg-white after:border-gray-300 
                  after:border after:rounded-full after:h-6 after:w-6 
                  after:transition-all peer-checked:after:translate-x-5"
                />
              </label>
            </div>
          </motion.div>
        ))}

        {/* Notification Preferences */}
        {settings.notifications && notificationPreferences && (
          <motion.div
            layout
            className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm"
          >
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
              {t('settings.notificationPreferences')}
            </h4>

            <div className="space-y-4">
              {/* Morning notifications */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-yellow-50 dark:bg-yellow-900/20">
                    <Sun className="w-5 h-5 text-yellow-500 dark:text-yellow-400" />
                  </div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {t('settings.morningNotifications')}
                  </span>
                </div>
                <div className="flex items-center space-x-4">
                  <select
                    value={notificationPreferences.morning.time}
                    onChange={(e) => handleHourChange('morning', parseInt(e.target.value))}
                    disabled={!notificationPreferences.morning.enabled || isLoading}
                    className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {hourOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificationPreferences.morning.enabled}
                      onChange={() => handleNotificationPeriodToggle('morning')}
                      disabled={isLoading}
                      className="sr-only peer"
                    />
                    <div className="w-12 h-7 bg-gray-200 dark:bg-gray-700 
                      peer-focus:outline-none peer-focus:ring-4 
                      peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 
                      rounded-full peer transition-colors
                      peer-checked:bg-blue-500 dark:peer-checked:bg-blue-600
                      after:content-[''] after:absolute after:top-[2px] 
                      after:left-[2px] after:bg-white after:border-gray-300 
                      after:border after:rounded-full after:h-6 after:w-6 
                      after:transition-all peer-checked:after:translate-x-5"
                    />
                  </label>
                </div>
              </div>

              {/* Evening notifications */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/20">
                    <Moon className="w-5 h-5 text-indigo-500 dark:text-indigo-400" />
                  </div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {t('settings.eveningNotifications')}
                  </span>
                </div>
                <div className="flex items-center space-x-4">
                  <select
                    value={notificationPreferences.evening.time}
                    onChange={(e) => handleHourChange('evening', parseInt(e.target.value))}
                    disabled={!notificationPreferences.evening.enabled || isLoading}
                    className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {hourOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificationPreferences.evening.enabled}
                      onChange={() => handleNotificationPeriodToggle('evening')}
                      disabled={isLoading}
                      className="sr-only peer"
                    />
                    <div className="w-12 h-7 bg-gray-200 dark:bg-gray-700 
                      peer-focus:outline-none peer-focus:ring-4 
                      peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 
                      rounded-full peer transition-colors
                      peer-checked:bg-blue-500 dark:peer-checked:bg-blue-600
                      after:content-[''] after:absolute after:top-[2px] 
                      after:left-[2px] after:bg-white after:border-gray-300 
                      after:border after:rounded-full after:h-6 after:w-6 
                      after:transition-all peer-checked:after:translate-x-5"
                    />
                  </label>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        <motion.div
          layout
          className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm"
        >
          <LanguageSelector
            variant="dropdown"
            currentLanguage={settings.language}
            onLanguageChange={(langCode) => {
              setSettings((prev) => ({ ...prev, language: langCode }));
            }}
            disabled={isLoading}
          />
        </motion.div>
      </div>
    </motion.section>
  );
}

export default AppSettings;